import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import { Feature } from '@sdk/common/features';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import useConfig from '@sdk/config/useConfig';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';

import getPremiumTooltipId from '../../../premiumGuard/getPremiumTooltipId';
import PremiumGuard from '../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { PremiumModalNames } from '../../../premiumGuard/constants/modalNames';
import { FIELD_WIDTH_205 } from '../../constants';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

import { getRestBreakThresholdHours, validateRestBreakThreshold } from './helpers/utils';
import { useLaborCompilance } from './LaborCompilance.hooks';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_205}px;
`;

const LaborCompilance: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { onChangeHandler } = useLaborCompilance();

  const {
    FEATURE_FLAGS: { CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED },
  } = useConfig();

  const clopeningSwitcherRow = (
    <SettingsSwitcherRow
      disabled={!currentUser.isAdmin}
      title={gettext('Clopening')}
      subtitle={gettext('Ensure adequate rest time between shifts.')}
      value={userOrgSettings.restBreakEnabled}
      onChange={(value): void => onUserOrgSettingsChange({ restBreakEnabled: value })}
      premiumGuard={
        CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED ? undefined : getPremiumGuardConfig(Feature.clopening)
      }
    />
  );

  const clopeningThreshold = (
    <RowSpaceBetween>
      <Column>
        <Text14 semiBold themeColor={ThemeColorType.gray}>
          {gettext('Rest hours between shifts')}
        </Text14>
        <Text14 themeColor={ThemeColorType.gray}>
          {gettext('Minimum number of rest hours between the closing and opening shifts:')}
        </Text14>
      </Column>

      <StyledTextField
        disabled={!currentUser.isAdmin}
        value={getRestBreakThresholdHours(userOrgSettings.restBreakThreshold)}
        onChange={(event): void => onChangeHandler('restBreakThreshold', event)}
        postfix={gettext('Hours')}
        skipErrorMessage
        invalid={!validateRestBreakThreshold(userOrgSettings.restBreakThreshold)}
      />
    </RowSpaceBetween>
  );

  return (
    <SettingsSection>
      <CollapsibleSettingsSection
        isActive={userOrgSettings.restBreakEnabled}
        headerItem={
          CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED ? (
            clopeningSwitcherRow
          ) : (
            <PremiumGuard
              premiumGuardModalName={PremiumModalNames.clopening}
              hasAccess={currentUser.org.isPremium}
              tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.clopening)]}>
              {clopeningSwitcherRow}
            </PremiumGuard>
          )
        }
        contentItem={
          <Column pTop={20}>
            {CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED ? (
              clopeningThreshold
            ) : (
              <PremiumGuard
                premiumGuardModalName={PremiumModalNames.clopening}
                hasAccess={currentUser.org.isPremium}
                tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.clopening)]}>
                {clopeningThreshold}
              </PremiumGuard>
            )}
          </Column>
        }
      />

      {CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED && (
        <>
          <Row pTop={20} pBottom={20}>
            <HorizontalSeparator />
          </Row>
          <CollapsibleSettingsSection
            isActive={userOrgSettings.consecutiveWorkDaysLimitEnabled}
            headerItem={
              <SettingsSwitcherRow
                disabled={!currentUser.isAdmin}
                title={gettext('Rest period')}
                subtitle={gettext('Consider a rest day when scheduling employees.')}
                value={userOrgSettings.consecutiveWorkDaysLimitEnabled}
                onChange={(value): void =>
                  onUserOrgSettingsChange({ consecutiveWorkDaysLimitEnabled: value })
                }
              />
            }
            contentItem={
              <Column pTop={20}>
                <RowSpaceBetween>
                  <Column>
                    <Text14 themeColor={ThemeColorType.gray}>
                      {gettext('Define the maximum number of consecutive work days')}
                    </Text14>
                  </Column>

                  <StyledTextField
                    disabled={!currentUser.isAdmin}
                    value={userOrgSettings.consecutiveWorkDaysLimitThreshold}
                    onChange={(event): void =>
                      onChangeHandler('consecutiveWorkDaysLimitThreshold', event)
                    }
                    postfix={gettext('Days')}
                    skipErrorMessage
                    invalid={!(userOrgSettings.consecutiveWorkDaysLimitThreshold > 0)}
                  />
                </RowSpaceBetween>
              </Column>
            }
          />
        </>
      )}
    </SettingsSection>
  );
};

export default LaborCompilance;
